import React, { useState, useEffect } from 'react';
import NavLinks from '../Navbar/NavLinks';
import ContactModal from '../ContactModal';
import img from '../../images/tnrf-logo-white-bg-removed.png';

const NavBar = () => {
  const [top, setTop] = useState(!window.scrollY);
  const [isOpen, setIsOpen] = useState(false);
  
  // Contact modal state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Contact modal handlers
  const handleContactClick = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  useEffect(() => {
    const scrollHandler = () => {
      window.pageYOffset > 10 ? setTop(false) : setTop(true);
    };
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  }, [top]);

  return (
    <>
      <nav
        className={`fixed top-0 w-full z-50 transition duration-300 ease-in-out hero-navbar ${
          !top ? 'hero-navbar-scrolled' : 'hero-navbar-top'
        }`}
      >
        {/* Top Header Section */}
        <div className="hero-navbar-header">
          <div className="container mx-auto px-4 sm:px-6 md:px-8">
            <div className="flex justify-between items-center py-2 sm:py-0">
              
              {/* Empty left side for balance on desktop and mobile */}
              <div className="flex items-center space-x-3">
                {/* Desktop Address Info - Left side for balance */}
                <div className="hidden lg:block text-left">
                  <div className="text-white/90 text-lg font-medium drop-shadow-md">
                    <div className="flex items-center space-x-2 mb-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>3153 Ady Rd, Street, MD</span>
                    </div>
                    <div className="text-md text-white/60 pl-7">
                      Harford County, Maryland
                    </div>
                  </div>
                </div>
              </div>

              {/* Logo positioned center - balanced padding on mobile and desktop */}
              <div className="flex items-center justify-center pl-12 lg:pl-0 lg:pr-12 xl:pr-16">
                <img 
                  src={img} 
                  alt="TRNF Logo" 
                  className="h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 xl:h-36 xl:w-36 cursor-pointer hover:scale-105 transition-transform duration-200" 
                  onClick={scrollToTop}
                />
              </div>

              {/* Mobile: Hamburger menu on right | Desktop: Full contact info */}
              <div className="flex items-center">
                {/* Mobile Menu Button - Far right on mobile, hidden on desktop */}
                <div className="lg:hidden">
                  <button
                    className="p-3 rounded-lg text-white hover:bg-white/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    onClick={handleClick}
                    aria-label={isOpen ? 'Close Menu' : 'Open Menu'}
                  >
                    <svg
                      className="h-6 w-6 fill-current"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                    >
                      {isOpen ? (
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z"
                        />
                      ) : (
                        <path
                          fillRule="evenodd"
                          d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"
                        />
                      )}
                    </svg>
                  </button>
                </div>
                
                {/* Desktop Contact - Contact Us Button */}
                <div className="hidden lg:flex items-center space-x-4">
                  <button
                    onClick={handleContactClick}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.82 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Contact Us</span>
                  </button>
                  
                  {/* Facebook Icon - Desktop only */}
                  <a href="https://www.facebook.com/therapeuticridingatnormandyfarm/" target="_blank" rel="noopener noreferrer"
                     className="w-16 h-16 bg-blue-600 rounded flex items-center justify-center hover:bg-blue-500 transition-colors">
                    <svg className="w-8 h-8 fill-current text-white" viewBox="0 0 24 24">
                      <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z"/>
                    </svg>
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Main Navigation Section - Desktop only */}
        <div className="hero-navbar-nav hidden lg:block">
          <div className="container mx-auto px-4 sm:px-6 md:px-8">
            <div className="flex justify-between items-center py-2 sm:py-3">
              
              {/* Desktop Navigation Links - Centered */}
              <div className="flex w-full justify-center">
                <NavLinks className="flex items-center space-x-8" />
              </div>

            </div>
          </div>
        </div>
        
        {/* Mobile Overlay */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black opacity-50 z-30 lg:hidden"
            onClick={handleClick}
          ></div>
        )}
        
        {/* Mobile Sidebar */}
        <div className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-gradient-to-br from-green-800 via-green-700 to-green-900 transform transition-transform duration-300 ease-in-out z-40 overflow-y-auto ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          {/* Mobile sidebar header */}
          <div className="flex items-center justify-between p-6 border-b border-white/20">
            <div className="flex items-center space-x-3">
              <img src={img} alt="TRNF Logo" className="h-12 w-12" />
              <div>
                <h2 className="text-xl font-bold text-white drop-shadow-lg">Menu</h2>
              </div>
            </div>
            <button
              onClick={closeMenu}
              className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close Menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mobile menu content - scrollable */}
          <div className="flex flex-col p-6 mt-4 pb-8 min-h-0">
            <NavLinks className="flex flex-col space-y-6" onLinkClick={closeMenu} />
            
            {/* Mobile contact section with Contact Us button */}
            <div className="mt-8 pt-6 border-t border-white/20">
              <div className="text-center space-y-4">
                
                {/* Contact Us Button */}
                <button
                  onClick={handleContactClick}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-3"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.82 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Contact Us</span>
                </button>
                
                {/* Social media */}
                <div className="pt-4">
                  <a href="https://www.facebook.com/therapeuticridingatnormandyfarm/" target="_blank" rel="noopener noreferrer"
                     className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-3">
                    <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                      <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z"/>
                    </svg>
                    <span>Follow Us on Facebook</span>
                  </a>
                </div>
                
                {/* Mobile Address Info */}
                <div className="pt-6 border-t border-white/20 mt-6">
                  <div className="text-center text-white">
                    <h4 className="text-xl font-semibold mb-3 text-white/90">Visit Us</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-lg font-medium">3153 Ady Rd, Street, MD</span>
                      </div>
                      <div className="text-md text-white/60 text-center">
                        Harford County, Maryland
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={closeContactModal}
          defaultSubject=""
          defaultTopic="General Inquiry"
        />
      )}
    </>
  );
};

export default NavBar;